/**
 * Action points: clickable areas that reveal bubbles. Handles create, click, keyboard focus, smooth scroll into view.
 */

import { scrollManager } from '../../../utils/scroll'
import { debugLog } from '../../../features/debug'
import type { SpeechBubbleContext } from './types'

// --- Smooth scroll to action point ---

/**
 * Performs smooth scroll to an action point using Lenis
 * Always uses Lenis for smooth animation - never falls back to instant scroll
 */
function performSmoothScrollToActionPoint(
  actionPoint: HTMLElement,
  rect: DOMRect,
  startScrollY: number,
  viewportHeight: number,
  preCalculatedTargetScrollY?: number,
): void {
  // Use pre-calculated target if provided, otherwise calculate it
  const targetScrollY = preCalculatedTargetScrollY ?? startScrollY + rect.top - viewportHeight / 2 + rect.height / 2

  // Calculate scroll distance to determine duration
  const scrollDistance = Math.abs(targetScrollY - startScrollY)
  const maxDistance = viewportHeight * 10 // 10 viewport heights
  // Duration scales with distance: minimum 0.8s, up to 3s for very long scrolls
  const duration = Math.max(0.8, Math.min(0.8 + (scrollDistance / maxDistance) * 2.2, 3))

  // Temporarily disable scroll lock to allow smooth scrolling to action points
  // This is important for accessibility - users need to reach action points even if they're past scroll lock
  const wasScrollLocked = scrollManager.isLimited()
  if (wasScrollLocked) {
    scrollManager.removeMaxScroll()
  }
  const lenis = (
    window as unknown as {
      __lenis?: { scrollTo: (y: number, opts?: { duration?: number; easing?: (t: number) => number }) => void }
    }
  ).__lenis
  if (lenis?.scrollTo) {
    lenis.scrollTo(targetScrollY, {
      duration,
      easing: (t: number) => 1 - Math.pow(1 - t, 3),
    })
  } else {
    // Lenis should always be available, but if not, use requestAnimationFrame-based smooth scroll
    // This is a fallback that still provides smooth animation (not instant)
    let startScroll = window.scrollY // Get current scroll position
    const startTime = performance.now()
    const scrollDuration = duration * 1000
    let animationFrameId: number | null = null

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / scrollDuration, 1)

      // Ease out cubic
      const easeOut = 1 - Math.pow(1 - progress, 3)

      const currentScroll = startScroll + (targetScrollY - startScroll) * easeOut

      // Use window.scrollTo - scroll lock is disabled, so this will work smoothly
      window.scrollTo(0, currentScroll)

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animateScroll)
      } else {
        // Animation complete - scroll lock will be recalculated automatically on next scroll/resize
        animationFrameId = null
      }
    }

    animationFrameId = requestAnimationFrame(animateScroll)
  }
}

export interface ActionPointCallbacks {
  onClick: (
    actionPoint: HTMLDivElement,
    bubbleIndex: number,
    xPercent: number,
    yPercent: number,
    wasKeyboard?: boolean
  ) => void
}

// --- Create action point (keyboard/pointer tracking, focus scroll) ---

export function createActionPoint(
  imageContainer: HTMLDivElement,
  xPercent: number,
  yPercent: number,
  bubbleIndex: number,
  callbacks: ActionPointCallbacks,
): HTMLDivElement {
  const actionPoint = document.createElement('div')
  actionPoint.className = 'action-point'
  actionPoint.style.left = `${xPercent}%`
  actionPoint.style.top = `${yPercent}%`
  actionPoint.innerHTML = `<div class="action-point-circle"></div>`

  actionPoint.addEventListener('click', () => {
    callbacks.onClick(actionPoint, bubbleIndex, xPercent, yPercent, false)
  })

  actionPoint.setAttribute('tabindex', '0')
  actionPoint.setAttribute('role', 'button')
  actionPoint.setAttribute('aria-label', 'Click to show bubble')

  // Track if focus was triggered by keyboard (for smooth scroll) vs mouse/touch (no scroll needed)
  let lastInteractionWasKeyboard = false
  let keyboardFocusTimeout: number | null = null
  let recentPointerEvent = false

  // Track Tab key at document level - this catches Tab before focus happens
  // This is the most reliable way to detect keyboard navigation
  const handleDocumentKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return
    debugLog('[ActionPoint] Document Tab', { recentPointerEvent, lastInteractionWasKeyboard })
    recentPointerEvent = false
    lastInteractionWasKeyboard = true
    if (keyboardFocusTimeout) clearTimeout(keyboardFocusTimeout)
    keyboardFocusTimeout = window.setTimeout(() => {
      lastInteractionWasKeyboard = false
    }, 500)
  }

  // Use capture phase to catch Tab key before it reaches the element
  document.addEventListener('keydown', handleDocumentKeyDown, true)

  // Track when element receives Tab key (as backup)
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      debugLog('[ActionPoint] KeyDown Tab on element', { recentPointerEvent, lastInteractionWasKeyboard })
      recentPointerEvent = false
      lastInteractionWasKeyboard = true
      if (keyboardFocusTimeout) clearTimeout(keyboardFocusTimeout)
      keyboardFocusTimeout = window.setTimeout(() => {
        lastInteractionWasKeyboard = false
      }, 500)
    } else if (e.key === 'Enter' || e.key === ' ') {
      lastInteractionWasKeyboard = false
    }
  }

  actionPoint.addEventListener('keydown', handleKeyDown)

  // Track mouse/touch events to detect non-keyboard interaction
  const handlePointerEvent = () => {
    debugLog('[ActionPoint] PointerEvent', { recentPointerEvent, lastInteractionWasKeyboard })
    recentPointerEvent = true
    if (keyboardFocusTimeout) {
      clearTimeout(keyboardFocusTimeout)
      keyboardFocusTimeout = null
    }
    setTimeout(() => {
      if (!keyboardFocusTimeout) recentPointerEvent = false
    }, 100)
  }

  actionPoint.addEventListener('mousedown', handlePointerEvent)
  actionPoint.addEventListener('touchstart', handlePointerEvent)

  // Scroll action point into view when it receives keyboard focus
  // We need to prevent the browser's default focus scroll and use Lenis smooth scroll instead
  // BUT only for keyboard navigation, not mouse/touch clicks
  let isHandlingFocusScroll = false
  let savedScrollPosition = 0
  let targetScrollY = 0
  let scrollInterceptor: (() => void) | null = null
  let lenisScrollStarted = false

  const win = window as Window & {
    __actionPointScrollState?: { activeScrollInterceptor: (() => void) | null; activeScrollElement: HTMLElement | null }
    __lenis?: {
      scrollTo: (y: number, opts?: { duration?: number; easing?: (t: number) => number }) => void
      scroll: number
    }
  }
  if (!win.__actionPointScrollState) {
    win.__actionPointScrollState = { activeScrollInterceptor: null, activeScrollElement: null }
  }
  const globalScrollState = win.__actionPointScrollState

  scrollInterceptor = () => {
    if (!isHandlingFocusScroll) return
    const currentScroll = window.scrollY
    const distanceFromSaved = Math.abs(currentScroll - savedScrollPosition)
    const distanceToTarget = Math.abs(currentScroll - targetScrollY)
    const distanceFromSavedToTarget = Math.abs(targetScrollY - savedScrollPosition)
    debugLog('[ActionPoint] ScrollInterceptor', {
      currentScroll,
      savedScrollPosition,
      targetScrollY,
      lenisScrollStarted,
    })

    if (!lenisScrollStarted) {
      if (distanceFromSaved > 5) window.scrollTo(0, savedScrollPosition)
    } else {
      const isMovingTowardsTarget = distanceToTarget < distanceFromSavedToTarget * 1.1
      const isSuddenJump = distanceFromSaved > 100
      if (isSuddenJump && !isMovingTowardsTarget) window.scrollTo(0, savedScrollPosition)
    }
  }

  actionPoint.addEventListener(
    'focus',
    () => {
      debugLog('[ActionPoint] Focus', { recentPointerEvent, lastInteractionWasKeyboard })

      const lenisInstance = win.__lenis
      if (
        lenisInstance &&
        globalScrollState.activeScrollElement &&
        globalScrollState.activeScrollElement !== actionPoint
      ) {
        lenisInstance.scrollTo(lenisInstance.scroll, { immediate: true } as { immediate: boolean })
        if (globalScrollState.activeScrollInterceptor) {
          window.removeEventListener('scroll', globalScrollState.activeScrollInterceptor)
          globalScrollState.activeScrollInterceptor = null
        }
        globalScrollState.activeScrollElement = null
      }

      const initialRect = actionPoint.getBoundingClientRect()
      const vh = window.innerHeight
      const vw = window.innerWidth
      const isOutOfViewport =
        initialRect.top < 0 || initialRect.bottom > vh || initialRect.left < 0 || initialRect.right > vw
      debugLog('[ActionPoint] Focus viewport', { isOutOfViewport })

      // Only scroll into view when focus came from keyboard (Tab). Never scroll on mouse/touch click.
      if (!lastInteractionWasKeyboard) return
      if (recentPointerEvent) return
      if (!isOutOfViewport) return

      lastInteractionWasKeyboard = false
      if (keyboardFocusTimeout) {
        clearTimeout(keyboardFocusTimeout)
        keyboardFocusTimeout = null
      }

      isHandlingFocusScroll = true
      lenisScrollStarted = false
      savedScrollPosition = window.scrollY
      const viewportHeight = window.innerHeight
      const rect = actionPoint.getBoundingClientRect()
      targetScrollY = savedScrollPosition + rect.top - viewportHeight / 2 + rect.height / 2
      debugLog('[ActionPoint] Focus scroll target', {
        targetScrollY,
        scrollDistance: Math.abs(targetScrollY - savedScrollPosition),
      })

      globalScrollState.activeScrollElement = actionPoint
      globalScrollState.activeScrollInterceptor = scrollInterceptor
      window.addEventListener('scroll', scrollInterceptor!, { passive: false, once: false })

      if (lenisInstance && typeof lenisInstance.scrollTo === 'function') {
        const scrollDistance = Math.abs(targetScrollY - savedScrollPosition)
        const maxDistance = viewportHeight * 10
        const duration = Math.max(0.8, Math.min(0.8 + (scrollDistance / maxDistance) * 2.2, 3))
        if (scrollManager.isLimited()) scrollManager.removeMaxScroll()
        lenisInstance.scrollTo(targetScrollY, { duration, easing: (t: number) => 1 - Math.pow(1 - t, 3) })

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            lenisScrollStarted = true
            setTimeout(() => {
              isHandlingFocusScroll = false
              if (scrollInterceptor) window.removeEventListener('scroll', scrollInterceptor)
              if (globalScrollState.activeScrollElement === actionPoint) {
                globalScrollState.activeScrollElement = null
                globalScrollState.activeScrollInterceptor = null
              }
            }, 400)
          })
        })
      } else {
        requestAnimationFrame(() => {
          performSmoothScrollToActionPoint(actionPoint, rect, savedScrollPosition, viewportHeight, targetScrollY)
        })
      }
    },
    { capture: true },
  )

  // Handle Enter/Space for activation (separate from Tab navigation)
  actionPoint.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      callbacks.onClick(actionPoint, bubbleIndex, xPercent, yPercent, true)
    }
  })

  imageContainer.appendChild(actionPoint)
  return actionPoint
}
